import type {
  CreateRecurringRule,
  CreateUsageRestriction,
  CreateWallet,
  CreateWalletAlert,
  CreateWalletTransaction,
  UpdateWallet,
} from '@monorepo/shared/schemas/wallets'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { WALLET_BALANCE_POLL_MS } from '@/shared/constants/polling'
import { walletService } from '../services/walletService'

interface ApiError {
  response?: {
    data?: {
      error?: {
        message?: string
      }
      message?: string
    }
  }
  message?: string
}

export const WALLET_QUERY_KEY = 'wallets'

// Main wallet hooks

export function useWalletByCustomerId(customerId: string) {
  return useQuery({
    queryKey: [WALLET_QUERY_KEY, 'customer', customerId],
    queryFn: () => walletService.getWalletByCustomerId(customerId),
    enabled: !!customerId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchOnWindowFocus: false,
  })
}

export function useWallet(walletId: string) {
  return useQuery({
    queryKey: [WALLET_QUERY_KEY, walletId],
    queryFn: () => walletService.getWalletById(walletId),
    enabled: !!walletId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  })
}

export function useWallets(page = 1, limit = 20) {
  return useQuery({
    queryKey: [WALLET_QUERY_KEY, 'list', { page, limit }],
    queryFn: () => walletService.listWallets({ page, limit }),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export function useWalletBalance(walletId: string) {
  return useQuery({
    queryKey: [WALLET_QUERY_KEY, walletId, 'balance'],
    queryFn: () => walletService.getWalletBalance(walletId),
    enabled: !!walletId,
    staleTime: 30 * 1000, // 30 seconds - balance changes frequently
    refetchInterval: WALLET_BALANCE_POLL_MS,
  })
}

// Transaction hooks

export function useWalletTransactions(walletId: string, page = 1, limit = 20) {
  return useQuery({
    queryKey: [WALLET_QUERY_KEY, walletId, 'transactions', { page, limit }],
    queryFn: () =>
      walletService.getWalletTransactions(walletId, { page, limit }),
    enabled: !!walletId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  })
}

export function useTransaction(walletId: string, transactionId: string) {
  return useQuery({
    queryKey: [WALLET_QUERY_KEY, walletId, 'transactions', transactionId],
    queryFn: () => walletService.getTransaction(walletId, transactionId),
    enabled: !!walletId && !!transactionId,
    staleTime: 5 * 60 * 1000, // 5 minutes - transactions don't change
  })
}

// Recurring Rules hooks

export function useRecurringRules(
  walletId: string,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: [WALLET_QUERY_KEY, walletId, 'recurring-rules'],
    queryFn: () => walletService.getRecurringRules(walletId),
    enabled: options?.enabled !== undefined ? options.enabled : !!walletId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

// Restrictions hooks

export function useRestrictions(walletId: string) {
  return useQuery({
    queryKey: [WALLET_QUERY_KEY, walletId, 'restrictions'],
    queryFn: () => walletService.getRestrictions(walletId),
    enabled: !!walletId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

// Mutation hooks

export function useCreateWallet() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (walletData: CreateWallet) =>
      walletService.createWallet(walletData),
    onSuccess: newWallet => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: [WALLET_QUERY_KEY] })
      queryClient.invalidateQueries({
        queryKey: [WALLET_QUERY_KEY, 'customer', newWallet.customer_id],
      })
      toast.success('Wallet created successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to create wallet'
      toast.error(errorMessage)
    },
  })
}

export function useUpdateWallet() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      walletId,
      updateData,
    }: {
      walletId: string
      updateData: UpdateWallet
    }) => walletService.updateWallet(walletId, updateData),
    onSuccess: updatedWallet => {
      // Invalidate related queries
      queryClient.invalidateQueries({
        queryKey: [WALLET_QUERY_KEY, updatedWallet.id],
      })
      queryClient.invalidateQueries({
        queryKey: [WALLET_QUERY_KEY, 'customer', updatedWallet.customer_id],
      })
      queryClient.invalidateQueries({
        queryKey: [WALLET_QUERY_KEY, updatedWallet.id, 'balance'],
      })
      toast.success('Wallet settings updated successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to update wallet'
      toast.error(errorMessage)
    },
  })
}

export function useDeleteWallet() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (walletId: string) => walletService.deleteWallet(walletId),
    onSuccess: (_, walletId) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: [WALLET_QUERY_KEY] })
      queryClient.removeQueries({ queryKey: [WALLET_QUERY_KEY, walletId] })
      toast.success('Wallet deleted successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to delete wallet'
      toast.error(errorMessage)
    },
  })
}

export function useAddCredits() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      walletId,
      transactionData,
    }: {
      walletId: string
      transactionData: CreateWalletTransaction
    }) => walletService.addCredits(walletId, transactionData),
    onSuccess: (transaction, { walletId }) => {
      // Invalidate related queries to refresh data
      queryClient.invalidateQueries({ queryKey: [WALLET_QUERY_KEY, walletId] })
      queryClient.invalidateQueries({
        queryKey: [WALLET_QUERY_KEY, walletId, 'balance'],
      })
      queryClient.invalidateQueries({
        queryKey: [WALLET_QUERY_KEY, walletId, 'transactions'],
      })

      // Also invalidate customer-specific wallet queries
      queryClient.invalidateQueries({
        queryKey: [WALLET_QUERY_KEY, 'customer'],
      })

      const amount = walletService.formatAmount(
        transaction.amount,
        transaction.currency
      )
      toast.success(`Successfully added ${amount} to wallet`)
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to add credits'
      toast.error(errorMessage)
    },
  })
}

export function useDeductCredits() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      walletId,
      amount,
      description,
    }: {
      walletId: string
      amount: number
      description?: string
    }) => walletService.deductCredits(walletId, amount, description),
    onSuccess: (transaction, { walletId }) => {
      // Invalidate related queries to refresh data
      queryClient.invalidateQueries({ queryKey: [WALLET_QUERY_KEY, walletId] })
      queryClient.invalidateQueries({
        queryKey: [WALLET_QUERY_KEY, walletId, 'balance'],
      })
      queryClient.invalidateQueries({
        queryKey: [WALLET_QUERY_KEY, walletId, 'transactions'],
      })

      // Also invalidate customer-specific wallet queries
      queryClient.invalidateQueries({
        queryKey: [WALLET_QUERY_KEY, 'customer'],
      })

      const amount = walletService.formatAmount(
        transaction.amount,
        transaction.currency
      )
      toast.success(`Successfully deducted ${amount} from wallet`)
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to deduct credits'
      toast.error(errorMessage)
    },
  })
}

// Recurring Rules mutations

export function useCreateRecurringRule() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      walletId,
      ruleData,
    }: {
      walletId: string
      ruleData: CreateRecurringRule
    }) => walletService.createRecurringRule(walletId, ruleData),
    onSuccess: (_, { walletId }) => {
      queryClient.invalidateQueries({
        queryKey: [WALLET_QUERY_KEY, walletId, 'recurring-rules'],
      })
      toast.success('Recurring rule created successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to create recurring rule'
      toast.error(errorMessage)
    },
  })
}

export function useUpdateRecurringRule() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      walletId,
      ruleId,
      ruleData,
    }: {
      walletId: string
      ruleId: string
      ruleData: Partial<CreateRecurringRule>
    }) => walletService.updateRecurringRule(walletId, ruleId, ruleData),
    onSuccess: (_, { walletId }) => {
      queryClient.invalidateQueries({
        queryKey: [WALLET_QUERY_KEY, walletId, 'recurring-rules'],
      })
      toast.success('Recurring rule updated successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to update recurring rule'
      toast.error(errorMessage)
    },
  })
}

export function useDeleteRecurringRule() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ walletId, ruleId }: { walletId: string; ruleId: string }) =>
      walletService.deleteRecurringRule(walletId, ruleId),
    onSuccess: (_, { walletId }) => {
      queryClient.invalidateQueries({
        queryKey: [WALLET_QUERY_KEY, walletId, 'recurring-rules'],
      })
      toast.success('Recurring rule deleted successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to delete recurring rule'
      toast.error(errorMessage)
    },
  })
}

export function useToggleRecurringRule() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      walletId,
      ruleId,
      isActive,
    }: {
      walletId: string
      ruleId: string
      isActive: boolean
    }) => walletService.toggleRecurringRule(walletId, ruleId, isActive),
    onSuccess: (_, { walletId, isActive }) => {
      queryClient.invalidateQueries({
        queryKey: [WALLET_QUERY_KEY, walletId, 'recurring-rules'],
      })
      toast.success(
        `Recurring rule ${isActive ? 'activated' : 'deactivated'} successfully`
      )
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to toggle recurring rule'
      toast.error(errorMessage)
    },
  })
}

// Restrictions mutations

export function useCreateRestriction() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      walletId,
      restrictionData,
    }: {
      walletId: string
      restrictionData: CreateUsageRestriction
    }) => walletService.createRestriction(walletId, restrictionData),
    onSuccess: (_, { walletId }) => {
      queryClient.invalidateQueries({
        queryKey: [WALLET_QUERY_KEY, walletId, 'restrictions'],
      })
      toast.success('Wallet restriction created successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to create restriction'
      toast.error(errorMessage)
    },
  })
}

export function useUpdateRestriction() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      walletId,
      restrictionId,
      restrictionData,
    }: {
      walletId: string
      restrictionId: string
      restrictionData: Partial<CreateUsageRestriction>
    }) =>
      walletService.updateRestriction(walletId, restrictionId, restrictionData),
    onSuccess: (_, { walletId }) => {
      queryClient.invalidateQueries({
        queryKey: [WALLET_QUERY_KEY, walletId, 'restrictions'],
      })
      toast.success('Restriction updated successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to update restriction'
      toast.error(errorMessage)
    },
  })
}

export function useDeleteRestriction() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      walletId,
      restrictionId,
    }: {
      walletId: string
      restrictionId: string
    }) => walletService.deleteRestriction(walletId, restrictionId),
    onSuccess: (_, { walletId }) => {
      queryClient.invalidateQueries({
        queryKey: [WALLET_QUERY_KEY, walletId, 'restrictions'],
      })
      toast.success('Restriction deleted successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to delete restriction'
      toast.error(errorMessage)
    },
  })
}

// Wallet Alert hooks

export function useWalletAlerts(
  walletId: string,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: [WALLET_QUERY_KEY, walletId, 'alerts'],
    queryFn: () => walletService.getWalletAlerts(walletId),
    enabled:
      options?.enabled !== undefined
        ? options.enabled && !!walletId
        : !!walletId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export function useCreateWalletAlert() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      walletId,
      alertData,
    }: {
      walletId: string
      alertData: CreateWalletAlert
    }) => walletService.createWalletAlert(walletId, alertData),
    onSuccess: (_, { walletId }) => {
      queryClient.invalidateQueries({
        queryKey: [WALLET_QUERY_KEY, walletId, 'alerts'],
      })
      toast.success('Wallet alert created successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to create wallet alert'
      toast.error(errorMessage)
    },
  })
}

export function useUpdateWalletAlert() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      walletId,
      alertId,
      alertData,
    }: {
      walletId: string
      alertId: string
      alertData: Partial<CreateWalletAlert>
    }) => walletService.updateWalletAlert(walletId, alertId, alertData),
    onSuccess: (_, { walletId }) => {
      queryClient.invalidateQueries({
        queryKey: [WALLET_QUERY_KEY, walletId, 'alerts'],
      })
      toast.success('Wallet alert updated successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to update wallet alert'
      toast.error(errorMessage)
    },
  })
}

export function useDeleteWalletAlert() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      walletId,
      alertId,
    }: {
      walletId: string
      alertId: string
    }) => walletService.deleteWalletAlert(walletId, alertId),
    onSuccess: (_, { walletId }) => {
      queryClient.invalidateQueries({
        queryKey: [WALLET_QUERY_KEY, walletId, 'alerts'],
      })
      toast.success('Wallet alert deleted successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to delete wallet alert'
      toast.error(errorMessage)
    },
  })
}
